// =====================================================
// Shared helpers used across work-panel components.
// When Charles ships his formatters, swap these imports.
// =====================================================

/** Capitalize first letter, lowercase rest. */
export const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** Words that stay lowercase in American English title case. */
const TITLE_CASE_MINOR = new Set([
  "a", "an", "the", "and", "but", "or", "for", "nor", "so", "yet",
  "at", "by", "in", "of", "on", "to", "up", "as", "from", "with",
]);

/** Convert a string to American English title case. */
export const titleCase = (str) => {
  if (!str) return str;
  const words = str.split(" ");
  return words.map((word, i) => {
    const isFirst = i === 0;
    const isLast = i === words.length - 1;
    if (word.includes("-")) {
      return word.split("-").map((part, j) => {
        const partFirst = isFirst && j === 0;
        const partLast = isLast && j === word.split("-").length - 1;
        if (partFirst || partLast) return cap(part);
        if (TITLE_CASE_MINOR.has(part.toLowerCase())) return part.toLowerCase();
        return cap(part);
      }).join("-");
    }
    const core = word.toLowerCase().replace(/[^a-z].*$/, "");
    if (isFirst || isLast) return cap(word);
    if (TITLE_CASE_MINOR.has(core)) return word.toLowerCase();
    return cap(word);
  }).join(" ");
};

/** Format a number with locale separators. */
export const fmtNum = (v) => Number(v ?? 0).toLocaleString();

/** Format a duration in days. */
export const fmtDays = (days) => {
  if (days == null) return "—";
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${fmtNum(days)} days`;
};

/** Format a YYYY-MM-DD string for display without timezone conversion. */
export const fmtDisplayDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
};

/** Format currency with $ and commas. */
export const fmtCurrency = (v) => `$${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Parse a JSON-encoded field safely; return the original value if it isn't a JSON
 *  object string. ClickUp migration left some fields (priority/tags/service) as raw
 *  JSON strings, so callers wrap values with this before rendering to avoid showing
 *  `{"id":"2","color":"#f8ae00","priority":"high",...}` as literal text. */
export const safeParseJson = (value) => {
  if (typeof value !== "string") return value;
  if (!value.startsWith("{")) return value;
  try { return JSON.parse(value); } catch { return value; }
};

/**
 * The new task's id out of an `fn_work_create` response (`createWork`'s `{ data }`).
 * The RPC may return the uuid directly, an object with an `id`, or an array whose first
 * element is that object. Returns null when none of those carries an id.
 * @param {{data?: unknown}|null|undefined} resp
 * @returns {string|null}
 */
export const createdTaskOf = (resp) => {
  const created = resp?.data;
  const obj = Array.isArray(created) ? created[0] : created;
  return typeof obj === "string" ? obj : obj?.id ?? null;
};

/**
 * `ops_work_types` rows → `{ value, label }` pipeline options, sorted by label. Rows with
 * no `work_type` / `code` are dropped.
 * @param {Array<{work_type?: string, code?: string, label?: string, name?: string}>} workTypes
 * @returns {{value: string, label: string}[]}
 */
export const toWorkTypeOptions = (workTypes) =>
  workTypes
    .map((t) => ({
      value: t.work_type ?? t.code,
      label: t.label ?? t.name,
    }))
    .filter((o) => o.value != null)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
